import { Routes, Route } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { ScrollToTop } from './components/utils/ScrollToTop';
import { HomePage } from './components/pages/HomePage';
import { FeaturesPage } from './components/pages/FeaturesPage';
import { PricingPage } from './components/pages/PricingPage';
import { EnterprisePage } from './components/pages/EnterprisePage';
import { ChangelogPage } from './components/pages/ChangelogPage';
import { DashboardPage } from './components/pages/DashboardPage';
import { DocsPage } from './components/pages/DocsPage';
import { BlogPage } from './components/pages/BlogPage';
import { BlogDetailPage } from './components/pages/BlogDetailPage';
import { StatusPage } from './components/pages/StatusPage';
import { DownloadPage } from './components/pages/DownloadPage';
import { StatusAdminPage } from './components/pages/StatusAdminPage';
import { ContactPage } from './components/pages/ContactPage';
import { CommunityPage } from './components/pages/CommunityPage';
import { AboutPage } from './components/pages/AboutPage';
import { CareersPage } from './components/pages/CareersPage';
import { TermsPage } from './components/pages/TermsPage';
import { PrivacyPage } from './components/pages/PrivacyPage';
import { SecurityPage } from './components/pages/SecurityPage';
import { AuthProvider } from './context/AuthContext';
import { AuthModalProvider, useAuthModal } from './context/AuthModalContext';
import { AuthModal } from './components/auth/AuthModal';

// Admin components
import { AdminLogin } from './components/admin/AdminLogin';
import { AdminLayout } from './components/admin/AdminLayout';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { AdminAnalytics } from './components/admin/AdminAnalytics';
import { AdminUsers } from './components/admin/AdminUsers';
import { AdminExtensions } from './components/admin/AdminExtensions';
import { AdminContent } from './components/admin/AdminContent';
import { AdminSettings } from './components/admin/AdminSettings';
import { AdminAPIProviders } from './components/admin/AdminAPIProviders';
import { AdminPricing } from './components/admin/AdminPricing';
import { AdminAIModels } from './components/admin/AdminAIModels';
import { ExtensionsAPI } from './pages/api/ExtensionsAPI';

// Global Auth Modal component
function GlobalAuthModal() {
  const { isOpen, closeAuthModal } = useAuthModal();
  return <AuthModal isOpen={isOpen} onClose={closeAuthModal} />;
}

function App() {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');

  useEffect(() => {
    // Check system preference
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const savedTheme = localStorage.getItem('ochtopus-theme') as 'dark' | 'light' | null;

    if (savedTheme) {
      setTheme(savedTheme);
    } else if (mediaQuery.matches) {
      setTheme('light');
    }
  }, []);

  useEffect(() => {
    document.documentElement.classList.remove('dark', 'light');
    document.documentElement.classList.add(theme);
    localStorage.setItem('ochtopus-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <AuthProvider>
      <AuthModalProvider>
        <GlobalAuthModal />
        <Routes>
        {/* API Routes - No Header/Footer */}
        <Route path="/api/extensions" element={<ExtensionsAPI />} />

        {/* Admin Routes - No Header/Footer */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="status" element={<StatusAdminPage />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="ai-models" element={<AdminAIModels />} />
          <Route path="api-providers" element={<AdminAPIProviders />} />
          <Route path="pricing" element={<AdminPricing />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="extensions" element={<AdminExtensions />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Public Routes - With Header/Footer */}
        <Route
          path="/*"
          element={
            <div className="min-h-screen flex flex-col">
              <ScrollToTop />
              <Header theme={theme} toggleTheme={toggleTheme} />
              <main className="flex-1">
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/features" element={<FeaturesPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/enterprise" element={<EnterprisePage />} />
                  <Route path="/changelog" element={<ChangelogPage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/docs" element={<DocsPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogDetailPage />} />
                  <Route path="/status" element={<StatusPage />} />
                  <Route path="/download" element={<DownloadPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/community" element={<CommunityPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/careers" element={<CareersPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/security" element={<SecurityPage />} />
                </Routes>
              </main>
              <Footer />
            </div>
          }
        />
      </Routes>
      </AuthModalProvider>
    </AuthProvider>
  );
}

export default App;
